import React, { useEffect, useState } from 'react';
import { Shield, Edit, Plus, RefreshCw, Trash2, AlertTriangle, Search, X, User } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const ExclusionsCharges = () => {
  const { filialeId, isConsolidated } = useFiliale();
  const [exclusions, setExclusions] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [filteredPersonnel, setFilteredPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [showPersonnelDropdown, setShowPersonnelDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const personnelDropdownRef = React.useRef(null);

  const fetchExclusions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        params.filiale_id = filialeId;
      }
      const response = await http.get('/api/exclusions-charges', { params });
      setExclusions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur récupération exclusions:', error);
      setExclusions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonnel = async () => {
    try {
      const params = {};
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        params.filiale_id = filialeId;
      }
      const response = await http.get('/api/personnel', { params });
      setPersonnel(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Erreur récupération personnel:', error);
      setPersonnel([]);
    }
  };

  useEffect(() => {
    fetchExclusions();
    fetchPersonnel();
  }, [filialeId, isConsolidated]);

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (personnelDropdownRef.current && !personnelDropdownRef.current.contains(event.target)) {
        setShowPersonnelDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtrer la liste du personnel
  useEffect(() => {
    if (searchTerm) {
      const filtered = personnel.filter(emp => 
        emp.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.matricule.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPersonnel(filtered);
    } else {
      setFilteredPersonnel(personnel);
    }
  }, [searchTerm, personnel]);

  const handleSelectPersonnel = (emp) => {
    setFormData({ ...formData, personnel_id: emp.idpersonnel });
    setShowPersonnelDropdown(false);
    setSearchTerm('');
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      personnel_id: '',
      type_charge: 'cmu',
      type_exclusion: 'exempte',
      valeur_fixe: '',
      motif: '',
      actif: 1
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette exclusion ?')) return;

    try {
      await http.delete(`/api/exclusions-charges/${id}`);
      fetchExclusions();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.personnel_id) {
      alert('Veuillez sélectionner un employé');
      return;
    }
    
    try {
      const data = {
        ...formData,
        filiale_id: filialeId === 'all' ? null : filialeId
      };
      
      if (editingItem) {
        await http.put(`/api/exclusions-charges/${editingItem.id}`, data);
      } else {
        await http.post('/api/exclusions-charges', data);
      }
      setShowModal(false);
      fetchExclusions();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const getTypeChargeLabel = (type) => {
    const labels = {
      cmu: 'CMU',
      cnps: 'CNPS',
      impot_salaire: 'Impôt salaire',
      igr: 'IGR',
      prime_anciennete: 'Prime ancienneté',
      prime_variable: 'Prime variable'
    };
    return labels[type] || type;
  };

  const getTypeExclusionLabel = (type) => {
    const labels = {
      exempte: 'Exempté (fixe)',
      zero: 'Toujours 0',
      fixe: 'Montant fixe'
    };
    return labels[type] || type;
  };

  const getPersonnelName = (id) => {
    const emp = personnel.find(p => p.idpersonnel === parseInt(id));
    return emp ? `${emp.nom} ${emp.prenom}` : 'Inconnu';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Exclusions de Charges"
        subtitle="Gestion des exclusions et exemptions de charges de paie"
        icon={Shield}
        actions={[
          {
            label: 'Créer',
            icon: Plus,
            onClick: handleCreate,
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchExclusions,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8 space-y-6">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex h-48 items-center justify-center text-sm text-gray-500">
              Chargement…
            </div>
          ) : exclusions.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-gray-500">
              Aucune exclusion configurée.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Personnel</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type de charge</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type d'exclusion</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valeur fixe</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Motif</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {exclusions.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {getPersonnelName(item.personnel_id)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getTypeChargeLabel(item.type_charge)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          item.type_exclusion === 'zero' 
                            ? 'bg-red-100 text-red-600' 
                            : item.type_exclusion === 'exempte'
                            ? 'bg-yellow-100 text-yellow-600'
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          {getTypeExclusionLabel(item.type_exclusion)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.valeur_fixe || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.motif || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          item.actif 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {item.actif ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <button 
                            type="button" 
                            onClick={() => handleEdit(item)} 
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            type="button" 
                            onClick={() => handleDelete(item.id)} 
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl border border-blue-100">
              <div className="mb-4 flex items-center justify-between border-b border-blue-100 pb-4">
                <h3 className="text-lg font-bold text-blue-900">
                  {editingItem ? 'Modifier' : 'Créer'} Exclusion
                </h3>
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="text-gray-500 hover:text-gray-900"
                >
                  ✕
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div className="relative" ref={personnelDropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Personnel
                    </label>
                    <div
                      onClick={() => setShowPersonnelDropdown(!showPersonnelDropdown)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer flex items-center justify-between"
                    >
                      <span className={formData.personnel_id ? 'text-gray-900' : 'text-gray-500'}>
                        {formData.personnel_id ? getPersonnelName(formData.personnel_id) : 'Choisir un employé'}
                      </span>
                      <User className="h-4 w-4 text-gray-400" />
                    </div>

                    {showPersonnelDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-auto">
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Rechercher..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="py-1">
                          {filteredPersonnel.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              Aucun employé trouvé
                            </div>
                          ) : (
                            filteredPersonnel.map((emp) => (
                              <div
                                key={emp.idpersonnel}
                                onClick={() => handleSelectPersonnel(emp)}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                              >
                                <div className="font-medium text-gray-900">
                                  {emp.nom} {emp.prenom}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {emp.matricule}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type de charge
                    </label>
                    <select
                      required
                      value={formData.type_charge}
                      onChange={(e) => setFormData({ ...formData, type_charge: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="cmu">CMU</option>
                      <option value="cnps">CNPS</option>
                      <option value="impot_salaire">Impôt salaire</option>
                      <option value="igr">IGR</option>
                      <option value="prime_anciennete">Prime ancienneté</option>
                      <option value="prime_variable">Prime variable</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type d'exclusion
                    </label>
                    <select
                      required
                      value={formData.type_exclusion}
                      onChange={(e) => setFormData({ ...formData, type_exclusion: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="exempte">Exempté (cotisation fixe)</option>
                      <option value="zero">Toujours 0</option>
                      <option value="fixe">Montant fixe personnalisé</option>
                    </select>
                  </div>

                  {formData.type_exclusion === 'fixe' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Valeur fixe
                      </label>
                      <input
                        type="number"
                        value={formData.valeur_fixe}
                        onChange={(e) => setFormData({ ...formData, valeur_fixe: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Montant fixe"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Motif
                    </label>
                    <input
                      type="text"
                      value={formData.motif}
                      onChange={(e) => setFormData({ ...formData, motif: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Motif de l'exclusion"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="actif"
                      checked={formData.actif}
                      onChange={(e) => setFormData({ ...formData, actif: e.target.checked ? 1 : 0 })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="actif" className="ml-2 text-sm text-gray-700">
                      Actif
                    </label>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    >
                      {editingItem ? 'Modifier' : 'Créer'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExclusionsCharges;
